import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  CircularProgress,
  Paper,
  Grid,
  Alert,
  MenuItem,
  InputAdornment,
  Stack,
  Divider,
  Skeleton,
} from "@mui/material";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import { useAuthContext } from "../context/AuthContext";
import { fetchData } from "../api/client";
import { useCompanies } from "../hooks/useCompanies";

const TIPOS = [
  { value: 0, label: "Default" },
  { value: 1, label: "Correios" },
  { value: 2, label: "Vuxx" },
  { value: 3, label: "Retirada" },
  { value: 4, label: "Gestão de Envios" },
];

const onlyDigits = (s) => String(s || "").replace(/\D/g, "");

const TIPOS_VALIDACAO_CREDENCIAIS = new Set([1, 2, 4]); // Correios, Vuxx, Gestão de Envios

/** Tipos de veículo — Box 4 Rodas (lista da documentação da transportadora) */
const VUXX_VEHICLE_TYPES = [
  "MOTO",
  "FIORINO",
  "VAN",
  "HR",
  "IVECO",
  "3/4",
  "TOCO",
  "TRUCK",
];

function buildDaysLabel(minDays, maxDays, tipo) {
  if (Number(tipo) === 3) {
    const pronta = Number(maxDays);
    const encomenda = Number(minDays);

    if (pronta || encomenda) {
      const p = pronta ? `${pronta}d pronta` : "";
      const e = encomenda ? `${encomenda}d encomenda` : "";
      return [p, e].filter(Boolean).join(" / ");
    }
    return "Retirada";
  }

  const minOk = Number(minDays) > 0;
  const maxOk = Number(maxDays) > 0;
  if (minOk && maxOk) return `${Number(minDays)}-${Number(maxDays)}d`;
  if (minOk) return `${Number(minDays)}d`;
  if (maxOk) return `${Number(maxDays)}d`;
  return "3-7d";
}

function getSelectedCompanyIds() {
  try {
    const raw = sessionStorage.getItem("selectedCompanies");
    const arr = JSON.parse(raw || "[]");
    return Array.isArray(arr) ? arr.filter((n) => Number.isFinite(Number(n))).map(Number) : [];
  } catch {
    return [];
  }
}

const AddDeliveryMethodForm = ({ deliveryMethod, onClose, onSubmit, isFetching }) => {
  const { userData } = useAuthContext();

  const { data: compData, isLoading: companiesLoading } = useCompanies(1, 500, "");
  const companies = compData?.companies || [];

  const selectedCompanyIds = useMemo(() => getSelectedCompanyIds(), []);
  const defaultProjectId =
    selectedCompanyIds.length ? selectedCompanyIds[0] : companies[0]?.id ?? null;

  const [formData, setFormData] = useState(() => ({
    id: deliveryMethod?.id || undefined,
    id_projeto: deliveryMethod?.id_projeto ?? defaultProjectId ?? null,
    id_usuario: userData?.id || null,
    id_usuario_ultima_alteracao: userData?.id || null,
    nome: "",
    descricao: "",
    status: 1,
    tipo: 1,
    valor_minimo: "",
    valor_maximo: "",
    acrescimo: "",
    desconto: "",
    tempo_entrega_minimo: "",
    tempo_entrega_maximo: "",
    ceps_permitidos: "",
    cep_origem: "",
    chave: "",
    codigoservico: "",
    json: "",
  }));
  const [loadingById, setLoadingById] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [isValidatingCredentials, setIsValidatingCredentials] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [credentialValidation, setCredentialValidation] = useState(null);

  const markDirty = useCallback(() => {
    setHasUnsavedChanges(true);
    setCredentialValidation(null);
  }, []);

  const [vuxxConfig, setVuxxConfig] = useState({
    environment: "production",
    vehicleType: "FIORINO",
    originNumber: "1",
    originComplement: "",
    destComplement: "",
    shipperName: "Expedição",
    shipperDocument: "99999999999",
    driverAssistantsCount: 0,
    notesRoot: "XPro",
    warehouseTimeStart: "11:00:00",
    warehouseTimeEnd: "17:55:00",
    destTimeStart: "14:00:00",
    destTimeEnd: "18:55:00",
    merchandiseNotes: "Cotação e-commerce",
    customUrl: "",
  });
  /** Campos opcionais da transportadora (além do cadastro básico) */
  const [showVuxxExtras, setShowVuxxExtras] = useState(false);
  const [showVuxxAdvanced, setShowVuxxAdvanced] = useState(false);

  const projectOptions = useMemo(() => {
    const map = new Map(companies.map((c) => [Number(c.id), c]));
    const ids =
      selectedCompanyIds.length
        ? selectedCompanyIds
        : companies.length
        ? companies.map((c) => Number(c.id))
        : [];

    const base = ids.map((pid) => {
      const c = map.get(Number(pid));
      const labelBase = c?.nome || c?.fantasia || c?.razao_social || `Empresa #${pid}`;
      return { id: Number(pid), label: `${labelBase} (ID ${pid})` };
    });

    if (formData.id_projeto != null && !base.some((o) => o.id === Number(formData.id_projeto))) {
      base.unshift({
        id: Number(formData.id_projeto),
        label: `Empresa #${formData.id_projeto} (ID ${formData.id_projeto})`,
      });
    }

    return base;
  }, [companies, selectedCompanyIds, formData.id_projeto]);

  useEffect(() => {
    const id = deliveryMethod?.id;
    if (!id) return;
    setLoadingById(true);
    setLoadError(null);
    fetchData(`/delivery-methods/${id}`, "GET")
      .then((resp) => {
        const d = resp?.data || {};
        setFormData((prev) => ({
          ...prev,
          ...d,
          id,
          id_projeto: d.id_projeto ?? prev.id_projeto ?? defaultProjectId ?? null,
          id_usuario: d.id_usuario ?? prev.id_usuario ?? userData?.id ?? null,
          id_usuario_ultima_alteracao: userData?.id ?? prev.id_usuario_ultima_alteracao ?? null,
          nome: d.nome ?? "",
          descricao: d.descricao ?? "",
          status: d.status ?? 1,
          tipo: d.tipo ?? 1,
          valor_minimo: d.valor_minimo ?? "",
          valor_maximo: d.valor_maximo ?? "",
          acrescimo: d.acrescimo ?? "",
          desconto: d.desconto ?? "",
          tempo_entrega_minimo: d.tempo_entrega_minimo ?? "",
          tempo_entrega_maximo: d.tempo_entrega_maximo ?? "",
          ceps_permitidos: d.ceps_permitidos ?? "",
          cep_origem: d.cep_origem ?? "",
          chave: d.chave ?? "",
          codigoservico: d.codigoservico ?? "",
          json: d.json ?? "",
        }));
        setCredentialValidation(null);
        setHasUnsavedChanges(false);

        if (Number(d.tipo) === 2 && d.json) {
          try {
            const parsed = JSON.parse(d.json);
            const url = typeof parsed?.url === "string" ? parsed.url : "";
            const j = parsed?.json && typeof parsed.json === "object" ? parsed.json : {};
            const whAddr = j?.warehouse?.address && typeof j.warehouse.address === "object" ? j.warehouse.address : {};
            setVuxxConfig((prevCfg) => ({
              ...prevCfg,
              customUrl: url || prevCfg.customUrl,
              vehicleType:
                typeof j.vehicleType === "string" && VUXX_VEHICLE_TYPES.includes(j.vehicleType)
                  ? j.vehicleType
                  : prevCfg.vehicleType,
              originNumber:
                typeof whAddr.number === "string" ? whAddr.number : prevCfg.originNumber,
              originComplement:
                typeof whAddr.complement === "string" ? whAddr.complement : prevCfg.originComplement,
              shipperName:
                typeof j?.warehouse?.shipper?.name === "string"
                  ? j.warehouse.shipper.name
                  : prevCfg.shipperName,
              driverAssistantsCount:
                typeof j.driverAssistantsCount === "number"
                  ? j.driverAssistantsCount
                  : prevCfg.driverAssistantsCount,
              notesRoot: typeof j.notes === "string" ? j.notes : prevCfg.notesRoot,
              warehouseTimeStart:
                typeof j?.warehouse?.timeWindow?.start === "string"
                  ? j.warehouse.timeWindow.start
                  : prevCfg.warehouseTimeStart,
              warehouseTimeEnd:
                typeof j?.warehouse?.timeWindow?.end === "string"
                  ? j.warehouse.timeWindow.end
                  : prevCfg.warehouseTimeEnd,
              destTimeStart:
                typeof j?.merchandises?.[0]?.timeWindow?.start === "string"
                  ? j.merchandises[0].timeWindow.start
                  : prevCfg.destTimeStart,
              destTimeEnd:
                typeof j?.merchandises?.[0]?.timeWindow?.end === "string"
                  ? j.merchandises[0].timeWindow.end
                  : prevCfg.destTimeEnd,
              shipperDocument:
                typeof j?.warehouse?.shipper?.nationalDocumentNumber === "string"
                  ? j.warehouse.shipper.nationalDocumentNumber
                  : prevCfg.shipperDocument,
              merchandiseNotes:
                typeof j?.merchandises?.[0]?.notes === "string"
                  ? j.merchandises[0].notes
                  : prevCfg.merchandiseNotes,
              destComplement:
                typeof j?.merchandises?.[0]?.address?.complement === "string"
                  ? j.merchandises[0].address.complement
                  : prevCfg.destComplement,
              environment:
                url && url.toLowerCase().includes("staging") ? "sandbox" : prevCfg.environment,
            }));
          } catch {
            // ignore parse errors; user pode usar modo avançado
          }
        }
      })
      .catch((e) => setLoadError(e?.response?.data?.message || "Falha ao carregar a forma de entrega."))
      .finally(() => setLoadingById(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deliveryMethod?.id]);

  const isTipo = (t) => Number(formData.tipo) === Number(t);

  const daysPreview = useMemo(
    () => buildDaysLabel(formData.tempo_entrega_minimo, formData.tempo_entrega_maximo, formData.tipo),
    [formData.tempo_entrega_minimo, formData.tempo_entrega_maximo, formData.tipo]
  );

  const buildVuxxJsonFromConfig = () => {
    const env = vuxxConfig.environment === "sandbox" ? "sandbox" : "production";
    let baseUrl = (vuxxConfig.customUrl || "").trim();
    if (baseUrl && /place-order/i.test(baseUrl)) {
      baseUrl = baseUrl.replace(/place-order/gi, "estimate-price");
    }
    if (!baseUrl) {
      const host =
        env === "sandbox" ? "https://staging.api.vuxx.com.br" : "https://api.vuxx.com.br";
      baseUrl = `${host}/shipments/deliveries/estimate-price`;
    }

    const originZip = onlyDigits(formData.cep_origem).slice(0, 8);
    const originNum = String(vuxxConfig.originNumber || "1").trim() || "1";
    /** Número da entrega vem do e-commerce no checkout; "1" só preenche o modelo até o merge na API. */
    const destNum = "1";
    const vehicleType = vuxxConfig.vehicleType || "FIORINO";
    const shipperName = (vuxxConfig.shipperName || "Expedição").trim() || "Expedição";
    const oc = (vuxxConfig.originComplement || "").trim();
    const destComp = (vuxxConfig.destComplement || "").trim();
    const shipDoc = onlyDigits(vuxxConfig.shipperDocument).slice(0, 14) || "99999999999";
    const dac = Math.min(3, Math.max(0, Math.floor(Number(vuxxConfig.driverAssistantsCount) || 0)));

    const payload = {
      url: baseUrl,
      method: "POST",
      header: {
        "Content-Type": "application/json",
        Authorization: "#TOKEN#",
        "x-api-key": "#APIKEY#",
      },
      json: {
        vehicleType,
        scheduledFor: "#TOMORROW_BR#",
        notes: (vuxxConfig.notesRoot || "XPro").trim() || "XPro",
        driverAssistantsCount: dac,
        warehouse: {
          address: {
            postalCode: originZip,
            number: originNum,
            ...(oc ? { complement: oc } : {}),
          },
          timeWindow: {
            start: vuxxConfig.warehouseTimeStart || "11:00:00",
            end: vuxxConfig.warehouseTimeEnd || "17:55:00",
          },
          shipper: {
            name: shipperName,
            nationalDocumentNumber: shipDoc,
          },
        },
        merchandises: [
          {
            trackingCode: "#TRACKING_QUOTE#",
            notes: (vuxxConfig.merchandiseNotes || "Cotação e-commerce XPro").trim(),
            totalWeight: "#TOTALWEIGHT_KG#",
            packagesAmount: "#PACKAGES_AMOUNT#",
            address: {
              postalCode: "#PEOPLELOCATIONZIP#",
              number: destNum,
              ...(destComp ? { complement: destComp } : {}),
            },
            timeWindow: {
              start: vuxxConfig.destTimeStart || "14:00:00",
              end: vuxxConfig.destTimeEnd || "18:55:00",
            },
            recipient: {
              name: "Destinatário",
              phoneNumber: "00000000000",
              nationalDocumentNumber: "00000000000000",
            },
          },
        ],
      },
    };

    return JSON.stringify(payload);
  };

  const hasStoredSecret = (field) => !!formData[`has_${field}`];
  const maskSecretField = (field) => {
    markDirty();
    setFormData((prev) => ({
      ...prev,
      [field]: "",
      [`has_${field}`]: false,
    }));
  };

  const validate = () => {
    if (!String(formData.nome).trim()) return "Informe o nome da forma de entrega.";
    if (formData.id_projeto == null) return "Empresa obrigatória.";
    if (![0, 1].includes(Number(formData.status))) return "Status inválido.";
    if (isTipo(1)) {
      if (onlyDigits(formData.cep_origem).length !== 8) return "CEP de origem (Correios) inválido.";
      if (!String(formData.chave).trim() && !hasStoredSecret("chave")) return "Token (Correios) obrigatório.";
      if (!String(formData.codigoservico).trim()) return "Código de serviço (Correios) obrigatório.";
    }
    if (isTipo(2)) {
      if (!String(formData.chave).trim() && !hasStoredSecret("chave"))
        return "Token de acesso é obrigatório.";
      if (onlyDigits(formData.cep_origem).length !== 8)
        return "CEP do armazém (8 dígitos) é obrigatório.";
      if (!String(vuxxConfig.originNumber || "").trim())
        return "Número do endereço de coleta (armazém) é obrigatório.";
    }
    if (isTipo(4)) {
      if (!String(formData.chave).trim() && !hasStoredSecret("chave")) return "Chave de integração (GE) obrigatória.";
    }
    return null;
  };

  const normalizePayload = () => {
    const p = { ...formData };
    p.status = Number(p.status) === 1 ? 1 : 0;
    p.tipo = Number(p.tipo) || 0;

    const numOrNull = (v) => (v === "" || v === null || v === undefined ? null : Number(v));
    const intOrNull = (v) => {
      if (v === "" || v === null || v === undefined) return null;
      const n = Number(v);
      return Number.isFinite(n) ? Math.trunc(n) : null;
    };

    p.valor_minimo = numOrNull(p.valor_minimo);
    p.valor_maximo = numOrNull(p.valor_maximo);
    p.acrescimo = numOrNull(p.acrescimo);
    p.desconto = numOrNull(p.desconto);

    p.tempo_entrega_minimo = intOrNull(p.tempo_entrega_minimo);
    p.tempo_entrega_maximo = intOrNull(p.tempo_entrega_maximo);

    p.cep_origem = onlyDigits(p.cep_origem);
    if (!p.codigoservico && Number(p.tipo) === 4) p.codigoservico = "4162";
    if (Number(p.tipo) === 2) {
      p.codigoservico = "";
      if (showVuxxAdvanced && String(p.json || "").trim()) {
        /* JSON avançado informado pelo usuário */
      } else {
        p.json = buildVuxxJsonFromConfig();
      }
    } else if (!p.json) {
      p.json = null;
    }
    if (!p.ceps_permitidos) p.ceps_permitidos = null;

    if (p.id_projeto == null) p.id_projeto = defaultProjectId ?? null;

    return p;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "cep_origem") {
      markDirty();
      setFormData((prev) => ({ ...prev, [name]: onlyDigits(value).slice(0, 8) }));
      return;
    }
    if (name === "tempo_entrega_minimo" || name === "tempo_entrega_maximo") {
      markDirty();
      setFormData((prev) => ({ ...prev, [name]: value.replace(/\D/g, "").slice(0, 3) }));
      return;
    }
    if (name === "tipo") {
      markDirty();
    }
    if (name === "id_projeto") {
      const v = Number(value);
      markDirty();
      setFormData((prev) => ({ ...prev, id_projeto: Number.isFinite(v) ? v : null }));
      return;
    }
    markDirty();
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleValidateCredentials = async () => {
    const tipoNum = Number(formData.tipo);
    if (!TIPOS_VALIDACAO_CREDENCIAIS.has(tipoNum)) return;
    setIsValidatingCredentials(true);
    setCredentialValidation(null);
    setErrorMessage(null);
    try {
      const payload = normalizePayload();
      if (formData?.id) payload.id = formData.id;
      const resp = await fetchData("/delivery-methods/validate", "POST", payload);
      const r = resp?.data;
      if (r && typeof r === "object" && "valid" in r) {
        setCredentialValidation({
          valid: !!r.valid,
          title: r.userTitle || (r.valid ? "Ok" : "Atenção"),
          detail: r.userDetail || "",
        });
      } else {
        setCredentialValidation({
          valid: false,
          title: "Resposta inesperada",
          detail: "Não foi possível interpretar o retorno do servidor.",
        });
      }
    } catch (error) {
      const msg =
        error?.response?.data?.message ||
        error?.message ||
        "Erro ao testar credenciais. Verifique os campos obrigatórios e tente novamente.";
      setCredentialValidation({
        valid: false,
        title: "Não foi possível testar",
        detail: msg,
      });
    } finally {
      setIsValidatingCredentials(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrorMessage(null);

    const err = validate();
    if (err) {
      setErrorMessage(err);
      setIsSubmitting(false);
      return;
    }

    try {
      const payload = normalizePayload();
      const resp = await onSubmit(payload);

      const savedId =
        resp?.data?.id ??
        resp?.data?.data?.id ??
        payload?.id ??
        null;
      if (savedId && !formData?.id) {
        setFormData((p) => ({ ...p, id: savedId }));
      }
      setHasUnsavedChanges(false);
    } catch (error) {
      setErrorMessage(error?.response?.data?.message || "Erro ao salvar a forma de entrega.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const contentLoading = loadingById && formData?.id;

  return (
    <Paper
      elevation={3}
      sx={{
        p: 3,
        width: "95%",
        mx: "auto",
        overflowX: "hidden",
      }}
    >
      <Stack direction="row" alignItems="baseline" justifyContent="space-between">
        <Typography variant="h6" gutterBottom>
          {formData?.id ? "Editar Forma de Entrega" : "Adicionar Forma de Entrega"}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Preview prazo: <b>{daysPreview}</b>
        </Typography>
      </Stack>

      {loadError && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="error">{loadError}</Alert>
        </Box>
      )}

      {errorMessage && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="error">{errorMessage}</Alert>
        </Box>
      )}

      {contentLoading ? (
        <Box sx={{ mt: 2 }}>
          <Skeleton variant="rounded" height={60} sx={{ mb: 1 }} />
          <Skeleton variant="rounded" height={60} sx={{ mb: 1 }} />
          <Skeleton variant="rounded" height={60} sx={{ mb: 1 }} />
          <Skeleton variant="rounded" height={120} />
        </Box>
      ) : (
        <form onSubmit={handleSubmit}>
          <Grid container spacing={2} sx={{ overflowX: "hidden" }}>
            {/* Nome */}
            <Grid item xs={12}>
              <TextField
                label="Nome"
                name="nome"
                value={formData.nome || ""}
                onChange={handleChange}
                fullWidth
                required
                inputProps={{ maxLength: 120 }}
              />
            </Grid>

            {/* Status | Tipo | Empresa */}
            <Grid item xs={12} md={4}>
              <TextField
                label="Status"
                name="status"
                value={formData.status}
                onChange={handleChange}
                fullWidth
                select
                required
              >
                <MenuItem value={1}>Ativo</MenuItem>
                <MenuItem value={0}>Inativo</MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                label="Tipo"
                name="tipo"
                value={formData.tipo}
                onChange={handleChange}
                fullWidth
                select
                required
              >
                {TIPOS.map((t) => (
                  <MenuItem key={t.value} value={t.value}>
                    {t.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                label="Empresa"
                name="id_projeto"
                value={formData.id_projeto ?? ""}
                onChange={handleChange}
                fullWidth
                select
                required
                disabled={companiesLoading}
                helperText={
                  companiesLoading
                    ? "Carregando empresas..."
                    : "Selecione a empresa à qual esta forma de entrega pertencerá"
                }
              >
                {projectOptions.map((opt) => (
                  <MenuItem key={opt.id} value={opt.id}>
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* Descrição */}
            {/* <Grid item xs={12}>
              <TextField
                label="Descrição (opcional)"
                name="descricao"
                value={formData.descricao || ""}
                onChange={handleChange}
                fullWidth
                multiline
                minRows={2}
              />
            </Grid> */}

            {/* Regras financeiras */}
            <Grid item xs={12} md={3}>
              <TextField
                label="Pedido Mínimo (R$)"
                name="valor_minimo"
                value={formData.valor_minimo ?? ""}
                onChange={handleChange}
                fullWidth
                type="number"
                inputProps={{ step: "0.01" }}
                InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                label="Pedido Máximo (R$)"
                name="valor_maximo"
                value={formData.valor_maximo ?? ""}
                onChange={handleChange}
                fullWidth
                type="number"
                inputProps={{ step: "0.01" }}
                InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                label="Acréscimo (R$)"
                name="acrescimo"
                value={formData.acrescimo ?? ""}
                onChange={handleChange}
                fullWidth
                type="number"
                inputProps={{ step: "0.01" }}
                InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }}
                helperText="Somado ao valor final do frete"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                label="Desconto (R$)"
                name="desconto"
                value={formData.desconto ?? ""}
                onChange={handleChange}
                fullWidth
                type="number"
                inputProps={{ step: "0.01" }}
                InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }}
                helperText="Subtraído do valor final do frete"
              />
            </Grid>

            {/* Prazo */}
            <Grid item xs={12} md={3}>
              <TextField
                label={
                  Number(formData.tipo) === 3
                    ? "Prazo de entrega sob encomenda (dias)"
                    : "Prazo Mínimo (dias)"
                }
                name="tempo_entrega_minimo"
                value={formData.tempo_entrega_minimo ?? ""}
                onChange={handleChange}
                fullWidth
                inputProps={{ inputMode: "numeric", pattern: "\\d*" }}
                helperText={
                  Number(formData.tipo) === 3
                    ? "Sob encomenda"
                    : ""
                }
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                label={
                  Number(formData.tipo) === 3
                    ? "Prazo de entrega pronta entrega (dias)"
                    : "Prazo Máximo (dias)"
                }
                name="tempo_entrega_maximo"
                value={formData.tempo_entrega_maximo ?? ""}
                onChange={handleChange}
                fullWidth
                inputProps={{ inputMode: "numeric", pattern: "\\d*" }}
                helperText={
                  Number(formData.tipo) === 3
                    ? "Pronta entrega"
                    : ""
                }
              />
            </Grid>

            {/* Cobertura por CEP */}
            <Grid item xs={12} md={6}>
              <TextField
                label="CEPs Permitidos (prefixos ou faixas)"
                name="ceps_permitidos"
                value={formData.ceps_permitidos || ""}
                onChange={handleChange}
                fullWidth
                placeholder="Ex.: 010-199, 20000-28999, 301, 40000000-41999999"
                helperText={
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ wordBreak: "break-word" }}>
                    <InfoOutlined fontSize="small" />
                    <span>
                      Prefixos (ex.: 301), CEPs exatos (8 dígitos) ou faixas (A-B). Separe por vírgula, espaço ou ponto e vírgula.
                    </span>
                  </Stack>
                }
              />
            </Grid>

            {/* Campos por tipo */}
            {Number(formData.tipo) === 1 && (
              <>
                <Grid item xs={12}>
                  <Divider textAlign="left">Correios (oficial)</Divider>
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    label="CEP de Origem"
                    name="cep_origem"
                    value={formData.cep_origem || ""}
                    onChange={handleChange}
                    fullWidth
                    inputProps={{ maxLength: 8, inputMode: "numeric", pattern: "\\d*" }}
                  />
                </Grid>
                <Grid item xs={12} md={5}>
                  {(() => {
                    const hasToken = hasStoredSecret("chave");
                    const isMasked = hasToken && !formData.chave;
                    return (
                      <TextField
                        label="Token de acesso (Correios)"
                        name="chave"
                        value={isMasked ? "••••••••" : formData.chave || ""}
                        onChange={isMasked ? undefined : handleChange}
                        fullWidth
                        type="password"
                        autoComplete="new-password"
                        helperText={
                          isMasked
                            ? "Valor já configurado. Clique para alterar."
                            : !formData.chave && hasToken
                            ? "Já existe um Token configurado. Preencha apenas se quiser trocar."
                            : undefined
                        }
                        InputProps={isMasked ? { readOnly: true } : undefined}
                        onClick={isMasked ? () => maskSecretField("chave") : undefined}
                        onFocus={isMasked ? () => maskSecretField("chave") : undefined}
                      />
                    );
                  })()}
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Código de Serviço"
                    name="codigoservico"
                    value={formData.codigoservico || ""}
                    onChange={handleChange}
                    fullWidth
                    placeholder="Ex.: 04014"
                  />
                </Grid>
              </>
            )}

            {Number(formData.tipo) === 2 && (
              <>
                <Grid item xs={12}>
                  <Divider textAlign="left">Box 4 Rodas (Vuxx)</Divider>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    O valor do frete é calculado pela transportadora com base no CEP de entrega, peso e quantidade dos
                    itens do carrinho, tipo de veículo e endereço de coleta (armazém). Na loja, nome, telefone, número e
                    complemento do destinatário são enviados automaticamente no checkout quando o cliente informa o
                    endereço. Consulte a{" "}
                    <a href="https://api.vuxx.com.br/openapi/" target="_blank" rel="noopener noreferrer">
                      documentação da transportadora
                    </a>{" "}
                    para detalhes dos campos.
                  </Typography>
                </Grid>
                <Grid item xs={12} md={8}>
                  {(() => {
                    const hasToken = hasStoredSecret("chave");
                    const isMasked = hasToken && !formData.chave;
                    return (
                      <TextField
                        label="Token de acesso"
                        name="chave"
                        value={isMasked ? "••••••••" : formData.chave || ""}
                        onChange={isMasked ? undefined : handleChange}
                        fullWidth
                        type="password"
                        autoComplete="new-password"
                        helperText={
                          isMasked
                            ? "Valor já configurado. Clique para alterar."
                            : !formData.chave && hasToken
                            ? "Preencha só se for trocar o token."
                            : "Credencial informada pela transportadora para calcular o frete."
                        }
                        InputProps={isMasked ? { readOnly: true } : undefined}
                        onClick={isMasked ? () => maskSecretField("chave") : undefined}
                        onFocus={isMasked ? () => maskSecretField("chave") : undefined}
                      />
                    );
                  })()}
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Ambiente"
                    name="vuxx_environment"
                    value={vuxxConfig.environment}
                    onChange={(e) =>
                      (markDirty(),
                      setVuxxConfig((prev) => ({
                        ...prev,
                        environment: e.target.value === "sandbox" ? "sandbox" : "production",
                      })))
                    }
                    fullWidth
                    select
                  >
                    <MenuItem value="production">Produção</MenuItem>
                    <MenuItem value="sandbox">Sandbox</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="CEP armazém (coleta)"
                    name="cep_origem"
                    value={formData.cep_origem || ""}
                    onChange={handleChange}
                    fullWidth
                    inputProps={{ maxLength: 8, inputMode: "numeric", pattern: "\\d*" }}
                    required
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Número (coleta)"
                    value={vuxxConfig.originNumber}
                    onChange={(e) =>
                      (markDirty(),
                      setVuxxConfig((prev) => ({
                        ...prev,
                        originNumber: e.target.value,
                      })))
                    }
                    fullWidth
                    required
                    helperText="Endereço de coleta exige CEP e número."
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Tipo de veículo"
                    value={vuxxConfig.vehicleType}
                    onChange={(e) =>
                      (markDirty(),
                      setVuxxConfig((prev) => ({
                        ...prev,
                        vehicleType: e.target.value,
                      })))
                    }
                    fullWidth
                    select
                    required
                  >
                    {VUXX_VEHICLE_TYPES.map((vt) => (
                      <MenuItem key={vt} value={vt}>
                        {vt}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12}>
                  <Button
                    variant="text"
                    size="small"
                    onClick={() => (markDirty(), setShowVuxxExtras((v) => !v))}
                  >
                    {showVuxxExtras ? "Ocultar opções adicionais" : "Opções adicionais"}
                  </Button>
                </Grid>
                {showVuxxExtras && (
                  <>
                    <Grid item xs={12}>
                      <TextField
                        label="Endereço do serviço (opcional)"
                        value={vuxxConfig.customUrl}
                        onChange={(e) =>
                          (markDirty(),
                          setVuxxConfig((prev) => ({
                            ...prev,
                            customUrl: e.target.value,
                          })))
                        }
                        fullWidth
                        placeholder="Deixe em branco para usar a cotação de frete padrão da transportadora."
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        label="Responsável pela coleta (nome)"
                        value={vuxxConfig.shipperName}
                        onChange={(e) =>
                          (markDirty(),
                          setVuxxConfig((prev) => ({
                            ...prev,
                            shipperName: e.target.value,
                          })))
                        }
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        label="CPF/CNPJ do responsável pela coleta"
                        value={vuxxConfig.shipperDocument}
                        onChange={(e) =>
                          (markDirty(),
                          setVuxxConfig((prev) => ({
                            ...prev,
                            shipperDocument: onlyDigits(e.target.value).slice(0, 14),
                          })))
                        }
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        label="Ajudantes no veículo (0 a 3)"
                        type="number"
                        inputProps={{ min: 0, max: 3 }}
                        value={vuxxConfig.driverAssistantsCount}
                        onChange={(e) =>
                          (markDirty(),
                          setVuxxConfig((prev) => ({
                            ...prev,
                            driverAssistantsCount: e.target.value,
                          })))
                        }
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        label="Observações da rota"
                        value={vuxxConfig.notesRoot}
                        onChange={(e) =>
                          (markDirty(),
                          setVuxxConfig((prev) => ({
                            ...prev,
                            notesRoot: e.target.value,
                          })))
                        }
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        label="Complemento coleta"
                        value={vuxxConfig.originComplement}
                        onChange={(e) =>
                          (markDirty(),
                          setVuxxConfig((prev) => ({
                            ...prev,
                            originComplement: e.target.value,
                          })))
                        }
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        label="Complemento padrão na entrega"
                        value={vuxxConfig.destComplement}
                        onChange={(e) =>
                          (markDirty(),
                          setVuxxConfig((prev) => ({
                            ...prev,
                            destComplement: e.target.value,
                          })))
                        }
                        fullWidth
                        helperText="Só se a loja não enviar complemento no checkout."
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField
                        label="Início da janela de coleta"
                        value={vuxxConfig.warehouseTimeStart}
                        onChange={(e) =>
                          (markDirty(),
                          setVuxxConfig((prev) => ({
                            ...prev,
                            warehouseTimeStart: e.target.value,
                          })))
                        }
                        fullWidth
                        placeholder="11:00:00"
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField
                        label="Fim da janela de coleta"
                        value={vuxxConfig.warehouseTimeEnd}
                        onChange={(e) =>
                          (markDirty(),
                          setVuxxConfig((prev) => ({
                            ...prev,
                            warehouseTimeEnd: e.target.value,
                          })))
                        }
                        fullWidth
                        placeholder="17:55:00"
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField
                        label="Início da janela de entrega"
                        value={vuxxConfig.destTimeStart}
                        onChange={(e) =>
                          (markDirty(),
                          setVuxxConfig((prev) => ({
                            ...prev,
                            destTimeStart: e.target.value,
                          })))
                        }
                        fullWidth
                        placeholder="14:00:00"
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField
                        label="Fim da janela de entrega"
                        value={vuxxConfig.destTimeEnd}
                        onChange={(e) =>
                          (markDirty(),
                          setVuxxConfig((prev) => ({
                            ...prev,
                            destTimeEnd: e.target.value,
                          })))
                        }
                        fullWidth
                        placeholder="18:55:00"
                      />
                    </Grid>
                    <Grid item xs={12} md={8}>
                      <TextField
                        label="Observação da mercadoria"
                        value={vuxxConfig.merchandiseNotes}
                        onChange={(e) =>
                          (markDirty(),
                          setVuxxConfig((prev) => ({
                            ...prev,
                            merchandiseNotes: e.target.value,
                          })))
                        }
                        fullWidth
                      />
                    </Grid>
                  </>
                )}
                <Grid item xs={12}>
                  <Button
                    variant="text"
                    size="small"
                    onClick={() => (markDirty(), setShowVuxxAdvanced((v) => !v))}
                  >
                    {showVuxxAdvanced ? "Ocultar configuração avançada" : "Configuração avançada (modelo da requisição)"}
                  </Button>
                </Grid>
                {showVuxxAdvanced && (
                  <Grid item xs={12}>
                    <TextField
                      label="Modelo da requisição (JSON)"
                      name="json"
                      value={formData.json || ""}
                      onChange={handleChange}
                      fullWidth
                      multiline
                      minRows={4}
                      helperText="Somente se precisar substituir o que foi gerado pelo formulário acima."
                    />
                  </Grid>
                )}
              </>
            )}

            {Number(formData.tipo) === 3 && (
              <>
                <Grid item xs={12}>
                  <Divider textAlign="left">Retirada</Divider>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Instruções ao cliente (opcional)"
                    name="json"
                    value={formData.json || ""}
                    onChange={handleChange}
                    fullWidth
                    multiline
                    minRows={2}
                    placeholder="Ex.: Endereço da loja, horários, documentos necessários…"
                  />
                </Grid>
              </>
            )}

            {Number(formData.tipo) === 4 && (
              <>
                <Grid item xs={12}>
                  <Divider textAlign="left">Gestão de Envios</Divider>
                </Grid>
                <Grid item xs={12} md={6}>
                  {(() => {
                    const hasKey = hasStoredSecret("chave");
                    const isMasked = hasKey && !formData.chave;
                    return (
                      <TextField
                        label="Chave de Integração"
                        name="chave"
                        value={isMasked ? "••••••••" : formData.chave || ""}
                        onChange={isMasked ? undefined : handleChange}
                        fullWidth
                        type="password"
                        autoComplete="new-password"
                        helperText={
                          isMasked
                            ? "Valor já configurado. Clique para alterar."
                            : !formData.chave && hasKey
                            ? "Já existe uma chave configurada. Preencha apenas se quiser trocar."
                            : undefined
                        }
                        InputProps={isMasked ? { readOnly: true } : undefined}
                        onClick={isMasked ? () => maskSecretField("chave") : undefined}
                        onFocus={isMasked ? () => maskSecretField("chave") : undefined}
                      />
                    );
                  })()}
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    label="CEP de Origem (opcional)"
                    name="cep_origem"
                    value={formData.cep_origem || ""}
                    onChange={handleChange}
                    fullWidth
                    inputProps={{ maxLength: 8, inputMode: "numeric", pattern: "\\d*" }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    label="Código de Serviço"
                    name="codigoservico"
                    value={formData.codigoservico || "4162"}
                    onChange={handleChange}
                    fullWidth
                  />
                </Grid>
              </>
            )}
          </Grid>

          {credentialValidation && (
            <Box sx={{ mt: 3, width: "100%" }}>
              <Alert severity={credentialValidation.valid ? "success" : "warning"}>
                <Typography variant="subtitle2" component="span" display="block" gutterBottom>
                  {credentialValidation.title}
                </Typography>
                <Typography variant="body2">{credentialValidation.detail}</Typography>
              </Alert>
            </Box>
          )}

          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 3 }}>
            {TIPOS_VALIDACAO_CREDENCIAIS.has(Number(formData.tipo)) && (
              <Button
                type="button"
                variant="outlined"
                color="secondary"
                onClick={handleValidateCredentials}
                disabled={
                  hasUnsavedChanges ||
                  isValidatingCredentials ||
                  isSubmitting ||
                  isFetching ||
                  contentLoading
                }
                sx={{ mr: "auto" }}
              >
                {isValidatingCredentials ? <CircularProgress size={20} /> : "Testar credenciais"}
              </Button>
            )}
            {TIPOS_VALIDACAO_CREDENCIAIS.has(Number(formData.tipo)) && hasUnsavedChanges && (
              <Typography variant="caption" sx={{ mr: "auto", alignSelf: "center", opacity: 0.75 }}>
                Salve as alterações para poder testar as credenciais.
              </Typography>
            )}
            <Button variant="outlined" onClick={onClose} disabled={isSubmitting || isFetching}>
              Cancelar
            </Button>
            <Button type="submit" variant="contained" color="primary" disabled={isSubmitting || isFetching}>
              {isFetching || isSubmitting ? <CircularProgress size={20} color="inherit" /> : "Salvar"}
            </Button>
          </Box>
        </form>
      )}
    </Paper>
  );
};

export default AddDeliveryMethodForm;
